import { Box, Text } from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

import { StepProgressionCondtion } from 'modules/processes/components/ProcessSidePanel'
import { Step } from 'modules/processes/domain'
import { DueDatesFields } from 'modules/processes/hooks/useProcesses'

interface Props {
  step: Step
  stepProgressionCondition: string
  stepProgressionConditionResourceKey: string
}

export type StepDirections = 'after' | 'before'
const TRANSLATIONS_BY_TYPE = {
  manualFirstStep: () => i18n.t('processes.process_settings.steps.starts.manual.first'),
  manual: () => i18n.t('processes.process_settings.steps.starts.manual.other'),
  automaticFirstStep: () => i18n.t('processes.process_settings.steps.starts.automatic.first'),
  automatic: () => i18n.t('processes.process_settings.steps.starts.automatic.other'),
  byDate: (resource: string) =>
    i18n.t('processes.process_settings.steps.starts.by_date.text_current_day', {
      title: '',
      resource,
    }),
  byDateWithDays: (days_count: number, when: string, resource: string) =>
    i18n.t('processes.process_settings.steps.starts.by_date.text', {
      days_count,
      when,
      resource,
    }),
}

const byDueDateFields = () => ({
  [DueDatesFields.ProcessStartDate]: i18n.t(
    'processes.process_settings.steps.starts.by_date.resource.process_start_date'
  ),
  [DueDatesFields.ContractStartDate]: i18n.t(
    'processes.process_settings.steps.starts.by_date.resource.contract_start_date'
  ),
  [DueDatesFields.ContractEndDate]: i18n.t(
    'processes.process_settings.steps.starts.by_date.resource.contract_end_date'
  ),
  [DueDatesFields.EmployeeTerminationDate]: i18n.t(
    'processes.process_settings.steps.starts.by_date.resource.employee_termination_date'
  ),
  [DueDatesFields.StepStartDate]: i18n.t(
    'processes.process_settings.steps.starts.by_date.resource.step_start_date'
  ),
  [DueDatesFields.TrainingCompletionDate]: i18n.t(
    'processes.process_settings.steps.starts.by_date.resource.training_completion_date'
  ),
  [DueDatesFields.ContractTrialEndDate]: i18n.t(
    'processes.process_settings.steps.starts.by_date.resource.contract_trial_end_date'
  ),
})

const RESOURCE_TRANSLATION = (resource: string) => {
  const dueDateFieldFromString = Object.values(DueDatesFields).find((value) => value === resource)

  const byResourceTranslations = byDueDateFields()

  if (!dueDateFieldFromString) return byResourceTranslations[DueDatesFields.ProcessStartDate]

  return byResourceTranslations[dueDateFieldFromString]
}

const TRANSLATIONS_BY_DIRECTION = () => ({
  before: i18n.t('processes.process_settings.steps.starts.when.before'),
  after: i18n.t('processes.process_settings.steps.starts.when.after'),
})

export const stepDescription = ({
  step,
  stepProgressionCondition,
  stepProgressionConditionResourceKey,
}: Props) => {
  const manualDescription = (): string => {
    if (step.position === 1) {
      return TRANSLATIONS_BY_TYPE.manualFirstStep()
    }

    return TRANSLATIONS_BY_TYPE.manual()
  }

  const automaticDescription = (): string => {
    if (step.position === 1) {
      return TRANSLATIONS_BY_TYPE.automaticFirstStep()
    }

    return TRANSLATIONS_BY_TYPE.automatic()
  }

  const when = (): string => {
    if (step.offsetDirection === 'before') {
      return TRANSLATIONS_BY_DIRECTION().before
    }

    return TRANSLATIONS_BY_DIRECTION().after
  }

  const byDateDescription = (): string => {
    if (!step.offsetAmount) {
      return TRANSLATIONS_BY_TYPE.byDate(RESOURCE_TRANSLATION(stepProgressionConditionResourceKey))
    }

    return TRANSLATIONS_BY_TYPE.byDateWithDays(
      step.offsetAmount,
      when(),
      RESOURCE_TRANSLATION(stepProgressionConditionResourceKey)
    )
  }

  switch (stepProgressionCondition) {
    case StepProgressionCondtion.Manual:
      return manualDescription()
    case StepProgressionCondtion.Automatic:
      return automaticDescription()
    case StepProgressionCondtion.ByDate:
      return byDateDescription()
    default:
      return ''
  }
}

export const StepDescription = (props: Props) => {
  const description = stepDescription(props)

  return (
    <Box flexDirection="row" gap="s4">
      <Text size="200" weight="semibold">
        {i18n.t('processes.process_settings.steps.starts.title')}
      </Text>
      <Text size="200">{description}</Text>
    </Box>
  )
}
